import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { TMSCommodity } from 'types/Load';
import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';

export type CreateCommodityResponse = {
  commodity: TMSCommodity;
  message: string;
};

export async function createCommodity(data: {
  commodity: TMSCommodity;
  tmsID: number;
  options?: {
    customerBK?: string;
  };
}): Promise<Result<CreateCommodityResponse, ApiError>> {
  try {
    const response = await axios.post<CreateCommodityResponse>(
      `/commodities`,
      data
    );

    return ok(response.data);
  } catch (error) {
    captureException(error, { functionName: 'createCommodity' });

    if (!isAxiosError(error)) {
      return err({ message: 'Oops, something went wrong!' });
    }

    if (error && isAxiosError(error) && error.response?.status === 401) {
      throw error;
    }

    if (error && error.message === 'Extension context invalidated.') {
      throw error;
    }

    // Extract error message, but sanitize technical details
    const rawErrorMessage =
      error.response?.data.message || 'Failed to create commodity';

    // Check if error message contains technical details (URLs, HTTP status codes, etc.)
    const isTechnicalError =
      rawErrorMessage.includes('http://') ||
      rawErrorMessage.includes('https://') ||
      rawErrorMessage.includes('POST ') ||
      rawErrorMessage.includes('GET ') ||
      rawErrorMessage.includes('400') ||
      rawErrorMessage.includes('500') ||
      rawErrorMessage.includes('Bad Request') ||
      rawErrorMessage.includes('Microsoft-Azure') ||
      rawErrorMessage.includes('Globaltranztms returned an error');

    // Use user-friendly message for technical errors, otherwise use the raw error message
    const errorMessage = isTechnicalError
      ? 'Failed to create commodity. Please check your input and try again.'
      : rawErrorMessage;

    return err({
      message: errorMessage,
      commodity: error.response?.data.commodity,
    });
  }
}
